import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useId, useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { RadioButton } from 'primereact/radiobutton';
import { DialogHeader } from '@/components/dialog/dialog-header';
import { useToastContext } from '@/layout/context/toast-context';
import { useSipCall } from '@/shared/providers/SipCallProvider';
export default function TransferCallDialog({ visible, onHide }) {
    const inputId = useId();
    const { showToast } = useToastContext();
    const { transferActiveCall, warmTransfer, startWarmTransfer, completeWarmTransfer, cancelWarmTransfer, } = useSipCall();
    const [kind, setKind] = useState('cold');
    const [destination, setDestination] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const reset = useCallback(() => {
        setDestination('');
        setSubmitting(false);
        setKind('cold');
    }, []);
    useEffect(() => {
        if (!visible)
            reset();
    }, [visible, reset]);
    const handleHide = useCallback(() => {
        if (submitting)
            return;
        void cancelWarmTransfer().catch(() => undefined);
        reset();
        onHide();
    }, [onHide, submitting, reset, cancelWarmTransfer]);
    const runCold = async () => {
        const trimmed = destination.trim();
        if (!trimmed) {
            showToast({
                severity: 'warn',
                summary: 'Number required',
                detail: 'Enter an extension or number to transfer the call.',
            });
            return;
        }
        setSubmitting(true);
        try {
            await transferActiveCall(trimmed);
            showToast({
                severity: 'success',
                summary: 'Cold transfer started',
                detail: `Blind transfer (REFER) to ${trimmed} — completing on the PBX.`,
            });
            reset();
            onHide();
        }
        catch (e) {
            const detail = e instanceof Error ? e.message : String(e);
            showToast({
                severity: 'error',
                summary: 'Transfer failed',
                detail: detail || 'The server did not accept the transfer.',
            });
        }
        finally {
            setSubmitting(false);
        }
    };
    const runStartWarm = async () => {
        const trimmed = destination.trim();
        if (!trimmed) {
            showToast({
                severity: 'warn',
                summary: 'Number required',
                detail: 'Enter the consult extension (agent B).',
            });
            return;
        }
        setSubmitting(true);
        try {
            await startWarmTransfer(trimmed);
            showToast({
                severity: 'info',
                summary: 'Warm transfer',
                detail: `Customer on hold — calling ${trimmed} for consult.`,
            });
        }
        catch (e) {
            const detail = e instanceof Error ? e.message : String(e);
            showToast({
                severity: 'error',
                summary: 'Consult failed',
                detail,
            });
        }
        finally {
            setSubmitting(false);
        }
    };
    const runCompleteWarm = async () => {
        setSubmitting(true);
        try {
            await completeWarmTransfer();
            showToast({
                severity: 'success',
                summary: 'Warm transfer',
                detail: 'Attended transfer sent — customer should connect to the consult party on the PBX.',
            });
            reset();
            onHide();
        }
        catch (e) {
            const detail = e instanceof Error ? e.message : String(e);
            showToast({
                severity: 'error',
                summary: 'Complete transfer failed',
                detail,
            });
        }
        finally {
            setSubmitting(false);
        }
    };
    const runCancelWarm = async () => {
        setSubmitting(true);
        try {
            await cancelWarmTransfer();
            showToast({
                severity: 'info',
                summary: 'Warm transfer cancelled',
                detail: 'Consult ended and customer call taken off hold.',
            });
            reset();
            onHide();
        }
        catch (e) {
            const detail = e instanceof Error ? e.message : String(e);
            showToast({
                severity: 'error',
                summary: 'Cancel failed',
                detail,
            });
        }
        finally {
            setSubmitting(false);
        }
    };
    const warmActive = warmTransfer.phase !== 'idle';
    const consultReady = warmTransfer.phase === 'consult_established';
    return (_jsx(Dialog, { header: _jsx(DialogHeader, { icon: "pi pi-share-alt", label: "Transfer call" }), visible: visible, modal: true, draggable: false, resizable: false, dismissableMask: !submitting, closable: !submitting, style: { width: '96vw', maxWidth: '32rem' }, onHide: handleHide, footer: _jsx("div", { className: "flex justify-content-end gap-2 flex-wrap", children: !warmActive ? (_jsxs(_Fragment, { children: [_jsx(Button, { type: "button", label: "Cancel", severity: "secondary", text: true, disabled: submitting, onClick: handleHide }), _jsx(Button, { type: "button", label: kind === 'cold' ? 'Transfer (cold)' : 'Start consult (warm)', loading: submitting, onClick: () => void (kind === 'cold' ? runCold() : runStartWarm()) })] })) : (_jsxs(_Fragment, { children: [_jsx(Button, { type: "button", label: "Cancel warm transfer", severity: "secondary", text: true, disabled: submitting, onClick: () => void runCancelWarm() }), _jsx(Button, { type: "button", label: "Complete transfer", loading: submitting, disabled: !consultReady, onClick: () => void runCompleteWarm() })] })) }), children: _jsx("div", { className: "flex flex-column gap-3", children: !warmActive ? (_jsxs(_Fragment, { children: [_jsxs("div", { className: "flex flex-column gap-2", children: [_jsx("span", { className: "block text-900 font-medium", children: "Transfer type" }), _jsx("div", { className: "flex align-items-center gap-3", children: _jsxs("div", { className: "flex align-items-center gap-2", children: [_jsx(RadioButton, { inputId: "transfer-kind-cold", checked: kind === 'cold', disabled: submitting, onChange: () => setKind('cold') }), _jsx("label", { htmlFor: "transfer-kind-cold", className: "cursor-pointer", children: "Cold \u2014 blind REFER; you leave the call on the PBX" })] }) }), _jsxs("div", { className: "flex align-items-center gap-2", children: [_jsx(RadioButton, { inputId: "transfer-kind-warm", checked: kind === 'warm', disabled: submitting, onChange: () => setKind('warm') }), _jsx("label", { htmlFor: "transfer-kind-warm", className: "cursor-pointer", children: "Warm \u2014 hold customer, consult agent B, then attended transfer" })] })] }), _jsxs("div", { children: [_jsx("label", { htmlFor: inputId, className: "block text-900 font-medium mb-2", children: kind === 'cold' ? 'Transfer to' : 'Consult (agent B) extension' }), _jsx(InputText, { id: inputId, value: destination, disabled: submitting, autoFocus: visible, className: "w-full", inputMode: "tel", autoComplete: "off", placeholder: "Extension or phone number\u2026", onChange: (e) => setDestination(e.target.value), onKeyDown: (e) => {
                                    if (e.key === 'Enter' && !submitting) {
                                        void (kind === 'cold' ? runCold() : runStartWarm());
                                    }
                                } })] }), _jsx("p", { className: "m-0 text-sm text-color-secondary line-height-3", children: kind === 'cold' ? (_jsxs(_Fragment, { children: ["Sends a SIP ", _jsx("strong", { children: "REFER" }), " for a blind transfer; when the PBX accepts it, your softphone hangs up automatically."] })) : (_jsxs(_Fragment, { children: ["Puts the active customer leg on ", _jsx("strong", { children: "hold" }), ", places an outbound consult call, then ", _jsx("strong", { children: "REFER with Replaces" }), " (attended transfer) when you complete. Requires PBX support for hold + attended transfer."] })) })] })) : (_jsxs("div", { className: "flex flex-column gap-2", children: [_jsx("p", { className: "m-0 font-medium text-900", children: "Warm transfer in progress" }), _jsxs("p", { className: "m-0 text-sm text-color-secondary", children: ["Target: ", _jsx("strong", { children: warmTransfer.destinationLabel ?? destination })] }), _jsxs("p", { className: "m-0 text-sm", children: [warmTransfer.phase === 'consult_ringing' && (_jsx(_Fragment, { children: "Ringing consult party \u2014 wait for answer to complete the transfer." })), warmTransfer.phase === 'consult_established' && (_jsxs(_Fragment, { children: ["Consult connected. Talk to agent B, then choose ", _jsx("strong", { children: "Complete transfer" }), ' ', "to move the customer, or ", _jsx("strong", { children: "Cancel warm transfer" }), " to return to the customer."] }))] })] })) }) }));
}
